import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

import { isUserRole, ROLES } from '../../utils/jwt';
import * as customerApi from '../../API/customerApi';

const SearchWrapper = styled(Box)`
  ${(props) => props.theme.breakpoints.up('md')} {
    min-width: 320px;
  }
  min-width: 200px;
`;

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const role = isUserRole();
  const debounced = useDebouncedValue(input, 250);

  const basePath = useMemo(() => {
    if (role === ROLES.ADMIN) return '/admin';
    if (role === ROLES.SALESPERSON) return '/salesperson';
    if (role === ROLES.PRODUCTION) return '/produktion';
    return '/admin';
  }, [role]);

  useEffect(() => {
    if (!debounced || debounced.length < 2) {
      setOptions([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    customerApi
      .listCustomers({ search: debounced, limit: 10 })
      .then((res) => {
        if (!active) return;
        const customers = (res?.data || res || []).map((c) => ({
          id: c.id,
          label: c.companyName || c.company_name || c.name || `Kund #${c.id}`,
          subtitle: c.org_nr || c.orgNr || '',
          type: 'customer',
        }));
        setOptions(customers);
      })
      .catch(() => {
        if (active) setOptions([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [debounced]);

  const handleSelect = (event, value) => {
    if (!value) return;
    if (value.type === 'customer') {
      navigate(`${basePath}/leverans/kundlista/${value.id}`);
      setInput('');
    }
  };

  return (
    <SearchWrapper>
      <Autocomplete
        size="small"
        freeSolo
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
        loading={loading}
        onInputChange={(_e, val) => setInput(val)}
        onChange={handleSelect}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Box component="span" sx={{ fontWeight: 500 }}>
                {option.label}
              </Box>
              {option.subtitle && (
                <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: 12 }}>
                  {option.subtitle}
                </Box>
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Sök kund, abonnemang..."
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.08)',
              borderRadius: 1,
              input: { color: 'inherit' },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </SearchWrapper>
  );
};

export default GlobalSearch;
